import React, { useState } from "react";
import { ChevronDown, ChevronUp, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation } from "@/lib/i18n";
import InfoTooltip from "@/components/portfolio/InfoTooltip";
import { fmtCurrency, fmtPercent, fmtNumber } from "@/components/portfolio/_format";

/**
 * ClosedPositionsTable — Phase 8.C-2.
 *
 * One row per fully-closed lifecycle returned from
 * `GET /api/portfolio/closed-positions`. Read-only — closed aggregates
 * cannot be edited in this phase; the only allowed mutation is opening
 * a brand-new lifecycle via the regular BUY flow (handled elsewhere).
 *
 * Layout:
 *   • Desktop  → wide table with every aggregate column.
 *   • Mobile   → vertical card list so no critical financial value gets
 *                truncated by horizontal overflow.
 *
 * Empty state collapses the section by default to keep the page tidy.
 */
export default function ClosedPositionsTable({ closedPositions = [], onViewHistory, previewMode = false }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(previewMode ? false : closedPositions.length > 0);
  const isEmpty = !closedPositions || closedPositions.length === 0;

  // Phase 8.WLNAV.v1.5 — Preview mode: render a purely static collapsed
  // header (title + count badge only). No toggle, no chevron, no
  // expansion. The section must look real but be non-interactive.
  if (previewMode) {
    return (
      <section
        data-testid="portfolio-closed-positions-section"
        data-preview-mode="true"
        className="rounded-2xl border border-white/10 bg-[rgba(18,18,23,0.85)] shadow-[var(--app-shadow-elev-1)] overflow-hidden"
      >
        <div
          className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-3"
          data-testid="portfolio-closed-positions-static-header"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="text-sm sm:text-base font-medium">{t("portfolio.closed.heading")}</div>
            <Badge variant="outline" className="text-[10px] tabular border-white/15 bg-white/[0.04]" data-testid="portfolio-closed-positions-count">
              {closedPositions.length}
            </Badge>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="portfolio-closed-positions-section"
      className="rounded-2xl border border-white/10 bg-[rgba(18,18,23,0.85)] shadow-[var(--app-shadow-elev-1)] overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-3 text-left hover:bg-white/[0.02] transition-colors"
        data-testid="portfolio-closed-positions-toggle"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-sm sm:text-base font-medium">{t("portfolio.closed.heading")}</div>
          <Badge variant="outline" className="text-[10px] tabular border-white/15 bg-white/[0.04]" data-testid="portfolio-closed-positions-count">
            {closedPositions.length}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          {expanded
            ? <>{t("portfolio.closed.collapse_hide")} <ChevronUp className="w-4 h-4" /></>
            : <>{t("portfolio.closed.collapse_show")} <ChevronDown className="w-4 h-4" /></>}
        </div>
      </button>
      {expanded ? (
        isEmpty ? (
          <div
            data-testid="portfolio-closed-positions-empty"
            className="px-4 sm:px-6 py-8 text-center text-sm text-muted-foreground"
          >
            {t("portfolio.closed.empty")}
          </div>
        ) : (
          <>
            <DesktopTable rows={closedPositions} onViewHistory={onViewHistory} t={t} />
            <MobileCards rows={closedPositions} onViewHistory={onViewHistory} t={t} />
          </>
        )
      ) : null}
    </section>
  );
}

function PLCell({ value, currency }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  const positive = value >= 0;
  return (
    <span className={`tabular ${positive ? "text-[#2BFF8A]" : "text-[#FF3DA6]"}`}>
      {fmtCurrency(value, { currency, signed: true })}
    </span>
  );
}

function PLPctCell({ value }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  const positive = value >= 0;
  return (
    <span className={`tabular ${positive ? "text-[#2BFF8A]" : "text-[#FF3DA6]"}`}>
      {value >= 0 ? "+" : ""}{fmtPercent(value)}
    </span>
  );
}

function HoldingPeriodCell({ days, status, t }) {
  // Phase 8.C-2 polish — never render a negative duration. The backend
  // now ships an explicit `holding_period_status` that the UI consults
  // to decide between a real "N day(s)" rendering and a localized
  // "Unavailable" fallback. We also defensively guard against legacy
  // rows that pre-date the fix (negative numeric days).
  const isUnavailable =
    days === null
    || days === undefined
    || (typeof days === "number" && days < 0)
    || (typeof status === "string" && status.startsWith("unavailable"));
  if (isUnavailable) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          className="tabular text-muted-foreground italic"
          data-testid="closed-holding-period-unavailable"
        >
          {t("portfolio.closed.holding_period_unavailable")}
        </span>
        <InfoTooltip
          text={t("portfolio.closed.holding_period_unavailable_tooltip")}
          label={t("portfolio.closed.holding_period_unavailable_tooltip_label")}
          testId="closed-holding-period-unavailable-info"
          side="top"
        />
      </span>
    );
  }
  return (
    <span className="tabular text-muted-foreground">
      {t("portfolio.closed.holding_period_days").replace("{days}", String(days))}
    </span>
  );
}

function DesktopTable({ rows, onViewHistory, t }) {
  return (
    <div className="hidden md:block overflow-x-auto" data-testid="portfolio-closed-positions-desktop">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-white/10">
            <TableHead>{t("portfolio.closed.col_ticker")}</TableHead>
            <TableHead>{t("portfolio.closed.col_company")}</TableHead>
            <TableHead>{t("portfolio.closed.col_first_buy")}</TableHead>
            <TableHead>{t("portfolio.closed.col_final_sale")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_total_shares")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_avg_buy")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_avg_sell")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_proceeds")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_cost_basis")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_fees")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_realized_pl")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_realized_pct")}</TableHead>
            <TableHead>{t("portfolio.closed.col_holding_period")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_transactions")}</TableHead>
            <TableHead className="text-right">{t("portfolio.closed.col_action")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((cp) => {
            const cur = cp.currency || "USD";
            return (
              <TableRow
                key={cp.closed_position_id}
                data-testid={`portfolio-closed-row-${cp.ticker}-${cp.closed_position_id?.slice(0, 8)}`}
                className="border-white/10"
              >
                <TableCell className="font-mono">{cp.ticker}</TableCell>
                <TableCell className="truncate max-w-[180px]">{cp.company_name || "—"}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{(cp.first_buy_date || "").slice(0, 10) || "—"}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{(cp.final_sale_date || "").slice(0, 10) || "—"}</TableCell>
                <TableCell className="text-right tabular">{fmtNumber(cp.total_shares_sold, { digits: 6 })}</TableCell>
                <TableCell className="text-right tabular">{fmtCurrency(cp.avg_buy_price, { currency: cur })}</TableCell>
                <TableCell className="text-right tabular">{fmtCurrency(cp.avg_sell_price, { currency: cur })}</TableCell>
                <TableCell className="text-right tabular">{fmtCurrency(cp.total_proceeds, { currency: cur })}</TableCell>
                <TableCell className="text-right tabular">{fmtCurrency(cp.total_cost_basis, { currency: cur })}</TableCell>
                <TableCell className="text-right tabular text-muted-foreground">{fmtCurrency(cp.total_fees, { currency: cur })}</TableCell>
                <TableCell className="text-right"><PLCell value={cp.realized_pl} currency={cur} /></TableCell>
                <TableCell className="text-right"><PLPctCell value={cp.realized_pl_pct} /></TableCell>
                <TableCell><HoldingPeriodCell days={cp.holding_period_days} status={cp.holding_period_status} t={t} /></TableCell>
                <TableCell className="text-right tabular">{cp.transaction_count || 0}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewHistory?.(cp)}
                    data-testid={`portfolio-closed-view-history-${cp.ticker}-${cp.closed_position_id?.slice(0, 8)}`}
                  >
                    <History className="w-3.5 h-3.5 mr-1.5" />
                    {t("portfolio.closed.view_history")}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function MobileCards({ rows, onViewHistory, t }) {
  return (
    <div className="md:hidden grid gap-3 p-3" data-testid="portfolio-closed-positions-mobile">
      {rows.map((cp) => {
        const cur = cp.currency || "USD";
        const positive = (cp.realized_pl || 0) >= 0;
        return (
          <div
            key={cp.closed_position_id}
            data-testid={`portfolio-closed-card-${cp.ticker}-${cp.closed_position_id?.slice(0, 8)}`}
            className="rounded-xl bg-card/60 backdrop-blur-xl border border-white/10 p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-mono text-base">{cp.ticker}</div>
                <div className="text-xs text-muted-foreground truncate max-w-[180px]">{cp.company_name || "—"}</div>
              </div>
              <div className="text-right">
                <div className={`tabular text-sm ${positive ? "text-[#2BFF8A]" : "text-[#FF3DA6]"}`}>
                  {fmtCurrency(cp.realized_pl, { currency: cur, signed: true })}
                </div>
                <div className="text-[11px] text-muted-foreground"><PLPctCell value={cp.realized_pl_pct} /></div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div className="text-muted-foreground">{t("portfolio.closed.col_first_buy")}</div>
              <div className="text-right tabular">{(cp.first_buy_date || "").slice(0, 10) || "—"}</div>
              <div className="text-muted-foreground">{t("portfolio.closed.col_final_sale")}</div>
              <div className="text-right tabular">{(cp.final_sale_date || "").slice(0, 10) || "—"}</div>
              <div className="text-muted-foreground">{t("portfolio.closed.col_total_shares")}</div>
              <div className="text-right tabular">{fmtNumber(cp.total_shares_sold, { digits: 6 })}</div>
              <div className="text-muted-foreground">{t("portfolio.closed.col_avg_buy")}</div>
              <div className="text-right tabular">{fmtCurrency(cp.avg_buy_price, { currency: cur })}</div>
              <div className="text-muted-foreground">{t("portfolio.closed.col_avg_sell")}</div>
              <div className="text-right tabular">{fmtCurrency(cp.avg_sell_price, { currency: cur })}</div>
              <div className="text-muted-foreground">{t("portfolio.closed.col_proceeds")}</div>
              <div className="text-right tabular">{fmtCurrency(cp.total_proceeds, { currency: cur })}</div>
              <div className="text-muted-foreground">{t("portfolio.closed.col_cost_basis")}</div>
              <div className="text-right tabular">{fmtCurrency(cp.total_cost_basis, { currency: cur })}</div>
              <div className="text-muted-foreground">{t("portfolio.closed.col_fees")}</div>
              <div className="text-right tabular text-muted-foreground">{fmtCurrency(cp.total_fees, { currency: cur })}</div>
              <div className="text-muted-foreground">{t("portfolio.closed.col_holding_period")}</div>
              <div className="text-right"><HoldingPeriodCell days={cp.holding_period_days} status={cp.holding_period_status} t={t} /></div>
              <div className="text-muted-foreground">{t("portfolio.closed.col_transactions")}</div>
              <div className="text-right tabular">{cp.transaction_count || 0}</div>
            </div>
            <div className="pt-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => onViewHistory?.(cp)}
                data-testid={`portfolio-closed-mobile-view-history-${cp.ticker}-${cp.closed_position_id?.slice(0, 8)}`}
              >
                <History className="w-3.5 h-3.5 mr-1.5" />
                {t("portfolio.closed.view_history")}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
